import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, NavLink, useHistory } from "react-router-dom";
import {
  FiChevronDown,
  FiExternalLink,
  FiKey,
  FiList,
  FiLogOut,
  FiPlus,
  FiUser,
} from "react-icons/fi";
import BrandLogo from "../../../components/BrandLogo/BrandLogo";
import {
  getDealerAvatarUrl,
  getDealerDisplayName,
  getDealerInitials,
} from "../../utils/dealerIdentity";
import { logoutDealerPortal } from "../../utils/portalIdentity";
import DashboardNotifications from "./DashboardNotifications";

const quickLinks = [
  { label: "Dashboard", to: "/dealers/dashbord", exact: true },
  { label: "Auctions", to: "/dealers/dashbord/auction" },
  { label: "Buy now", to: "/dealers/dashbord/buynow" },
  { label: "My bids", to: "/dealers/dashbord/mybids" },
];

const accountLinks = [
  { label: "Profile", to: "/dealers/dashbord/profile", icon: FiUser },
  {
    label: "Change password",
    to: "/dealers/dashbord/change-password",
    icon: FiKey,
  },
  { label: "Add watchlist", to: "/dealers/dashbord/watchlist", icon: FiPlus },
  {
    label: "Watchlist active",
    to: "/dealers/dashbord/watchlistActive",
    icon: FiList,
  },
];

function DashboardHeader({ auth, logOut }) {
  const history = useHistory();
  const dealerName = getDealerDisplayName(auth);
  const dealerInitials = getDealerInitials(auth);
  const dealerAvatar = getDealerAvatarUrl(auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const menuRef = useRef(null);
  const showAvatar = Boolean(dealerAvatar) && !avatarFailed;

  useEffect(() => {
    setAvatarFailed(false);
  }, [dealerAvatar]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <header className="dd-topbar">
      <div className="dd-topbar__brand">
        <BrandLogo
          variant="light"
          compact
          className="dd-topbar__logo"
          onClick={() => history.push("/dealers")}
        />
        <div className="dd-topbar__title-block">
          <span className="dd-topbar__eyebrow">Workspace</span>
          <span className="dd-topbar__title">Dealer desk</span>
        </div>
      </div>

      <nav className="dd-topbar__links" aria-label="Marketplace shortcuts">
        {quickLinks.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            exact={Boolean(item.exact)}
            className="dd-topbar__link"
            activeClassName="is-active"
          >
            {item.label}
          </NavLink>
        ))}
        <Link to="/dealers" className="dd-topbar__link dd-topbar__link--muted">
          Dealers home
        </Link>
        <Link to="/" className="dd-topbar__link dd-topbar__link--muted">
          <FiExternalLink size={14} aria-hidden="true" />
          Home
        </Link>
      </nav>

      <div className="dd-topbar__actions">
        <DashboardNotifications
          accountOpen={menuOpen}
          onOpen={() => setMenuOpen(false)}
        />
        <div className="dd-topbar__account" ref={menuRef}>
          <button
            type="button"
            className="dd-topbar__user"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={dealerName}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            <span className="dd-topbar__avatar" aria-hidden="true">
              {showAvatar ? (
                <img
                  src={dealerAvatar}
                  alt=""
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                dealerInitials
              )}
            </span>
            <span className="dd-topbar__user-meta">
              <span className="dd-topbar__user-label">Signed in</span>
              <span className="dd-topbar__user-name">{dealerName}</span>
            </span>
            <FiChevronDown
              className={`dd-topbar__user-caret${menuOpen ? " is-open" : ""}`}
              size={14}
              aria-hidden
            />
          </button>
          {menuOpen ? (
            <div className="dd-topbar__account-menu" role="menu">
              {accountLinks.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    exact
                    role="menuitem"
                    className="dd-topbar__account-item"
                    activeClassName="is-active"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon size={14} aria-hidden />
                    {item.label}
                  </NavLink>
                );
              })}
              <button
                type="button"
                role="menuitem"
                className="dd-topbar__account-item is-danger"
                onClick={() => {
                  setMenuOpen(false);
                  logOut();
                  history.replace("/login");
                }}
              >
                <FiLogOut size={14} aria-hidden />
                Log out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function DashboardFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="dd-footbar">
      <div className="dd-footbar__inner">
        <span className="dd-footbar__mark">ATP</span>
        <p className="dd-footbar__copy">
          © {year} AutoTrade Port · Dealer workspace
        </p>
        <div className="dd-footbar__links">
          <Link to="/dealers/terms">Terms</Link>
          <Link to="/dealers/privacypolicy">Privacy</Link>
          <Link to="/dealers/contactus">Support</Link>
        </div>
      </div>
    </footer>
  );
}

const mapDispatchToProps = (dispatch) => ({
  logOut: () => logoutDealerPortal(dispatch),
});

const ConnectedHeader = connect(null, mapDispatchToProps)(DashboardHeader);

export { ConnectedHeader as DashboardHeader, DashboardFooter };
